import { useState, useRef, useCallback, useMemo, useEffect, useId } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import {
	Pencil,
	Volume2,
	VolumeX,
	Loader2,
	AlertCircle,
	Play,
	Pause,
	Download,
	Upload,
	RefreshCw,
	Trash2,
	GripVertical,
	Check,
	X,
	Copy,
	Eye,
	EyeOff,
	Palette,
	ChevronDown,
} from "lucide-react";
import type { Scene, SubtitleSettings } from "@/types";
import type { AdditionalTimeField } from "@/types/application";
import { calculateSceneDuration } from "@/core/timeline";
import { errorMessages } from "@/lib/errorMessages";
import { logger } from "@/lib/logger";
import { AUDIO_VALIDATION, ADDITIONAL_TIME } from "@/lib/constants";
import { isAudioFile } from "@/lib/mediaFile";
import {
	mergeSubtitleStyle,
	validateSubtitleText,
	generateSubtitlePreview,
} from "@/lib/subtitleUtils";

interface SceneItemProps {
	scene: Scene;
	sceneOrder: number;
	editMode: "speech" | "subtitle";
	subtitleSettings: SubtitleSettings;
	globalSubtitleEnabled: boolean;
	onDelete: (id: string) => void;
	onPlayAudio: (audioUrl: string, sceneId: string) => void;
	onDeleteAudio: (sceneId: string) => void;
	onRegenerateAudio: (sceneId: string) => void;
	onUploadAudio: (sceneId: string, file: File) => void;
	onDownloadAudio: (sceneId: string) => void;
	onUpdateText: (sceneId: string, text: string) => void;
	onUpdateAdditionalTime: (
		sceneId: string,
		field: AdditionalTimeField,
		time: number
	) => void;
	onUpdateSubtitleText: (sceneId: string, text: string) => void;
	onToggleSubtitle: (sceneId: string, enabled: boolean) => void;
	onCopyTextToSubtitle: (sceneId: string) => void;
	onOpenSubtitleStyleModal: (sceneId: string) => void;
	playingAudioId: string | null;
}

export function SceneItem({
	scene,
	sceneOrder,
	editMode,
	subtitleSettings,
	globalSubtitleEnabled,
	onDelete,
	onPlayAudio,
	onDeleteAudio,
	onRegenerateAudio,
	onUploadAudio,
	onDownloadAudio,
	onUpdateText,
	onUpdateAdditionalTime,
	onUpdateSubtitleText,
	onToggleSubtitle,
	onCopyTextToSubtitle,
	onOpenSubtitleStyleModal,
	playingAudioId,
}: SceneItemProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: scene.id,
	});
	const audioInputRef = useRef<HTMLInputElement>(null);
	const [isEditingSpeech, setIsEditingSpeech] = useState(false);
	const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
	const [editSpeechText, setEditSpeechText] = useState(scene.text);
	const [editSubtitleText, setEditSubtitleText] = useState(
		scene.subtitleText
	);
	const [isDraggingAudio, setIsDraggingAudio] = useState(false);
	// スマートフォン幅では既定で畳む。lg 以上では CSS 側で常に開いた表示になる
	const [expanded, setExpanded] = useState(false);
	// 開閉ボタンと開閉領域を aria-controls で結ぶための ID
	const timingControlsId = useId();
	const [imageSize, setImageSize] = useState<{
		width: number;
		height: number;
	} | null>(null);

	const { baseTime, beforeTime, afterTime, total: totalTime } =
		calculateSceneDuration(scene, sceneOrder === 0);

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	// マージしたスタイルを計算
	const mergedStyle = useMemo(
		() =>
			mergeSubtitleStyle(
				subtitleSettings.defaultStyle,
				scene.subtitleStyle
			),
		[subtitleSettings.defaultStyle, scene.subtitleStyle]
	);

	// 画像サイズを取得
	useEffect(() => {
		const img = new Image();
		img.onload = () => {
			setImageSize({
				width: img.naturalWidth,
				height: img.naturalHeight,
			});
		};
		img.src = scene.imageUrl;
	}, [scene.imageUrl]);

	// テロップ検証
	const subtitleValidation = useMemo(() => {
		if (!scene.subtitleText || !imageSize) return null;
		return validateSubtitleText(
			scene.subtitleText,
			imageSize.width,
			subtitleSettings,
			mergedStyle
		);
	}, [scene.subtitleText, subtitleSettings, mergedStyle, imageSize]);

	// Canvas ベースのサムネイルプレビュー（動画生成と同じロジック）
	const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(
		null
	);

	useEffect(() => {
		// テロップが有効でない場合はプレビュー不要
		const shouldGeneratePreview =
			globalSubtitleEnabled &&
			scene.subtitleEnabled &&
			scene.subtitleText &&
			imageSize;

		if (!shouldGeneratePreview) {
			// 非同期処理として実行（リントエラー回避）
			Promise.resolve().then(() => setThumbnailPreview(null));
			return;
		}

		// Canvas プレビューを生成（実際の画像サイズを使用）
		generateSubtitlePreview(
			scene.imageUrl,
			scene.subtitleText,
			subtitleSettings,
			mergedStyle,
			imageSize.width,
			imageSize.height
		)
			.then((result) => {
				setThumbnailPreview(result.dataUrl);
			})
			.catch((error) => {
				console.error("Failed to generate thumbnail preview:", error);
				setThumbnailPreview(null);
			});
	}, [
		scene.imageUrl,
		scene.subtitleText,
		scene.subtitleEnabled,
		globalSubtitleEnabled,
		subtitleSettings,
		mergedStyle,
		imageSize,
	]);

	const validateAudioFile = useCallback((file: File): boolean => {
		if (!isAudioFile(file)) {
			logger.warn(`サポートされていない音声形式: ${file.type}`, {
				fileName: file.name,
			});
			const errMsg = errorMessages.unsupportedAudioFormat(
				file.name,
				file.type
			);
			toast.error(errMsg.title, { description: errMsg.message });
			return false;
		}

		if (file.size > AUDIO_VALIDATION.MAX_SIZE) {
			logger.warn(
				`音声ファイルが大きすぎます: ${(
					file.size /
					1024 /
					1024
				).toFixed(2)}MB`,
				{
					fileName: file.name,
					maxSize: `${AUDIO_VALIDATION.MAX_SIZE / 1024 / 1024}MB`,
				}
			);
			const errMsg = errorMessages.audioFileTooLarge(
				file.name,
				file.size,
				AUDIO_VALIDATION.MAX_SIZE
			);
			toast.error(errMsg.title, { description: errMsg.message });
			return false;
		}

		return true;
	}, []);

	const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file && validateAudioFile(file)) {
			logger.info("音声ファイルをアップロード", {
				fileName: file.name,
				fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
				sceneId: scene.id,
			});
			onUploadAudio(scene.id, file);
			if (audioInputRef.current) {
				audioInputRef.current.value = "";
			}
		}
	};

	const handleSaveSpeechText = () => {
		onUpdateText(scene.id, editSpeechText);
		setIsEditingSpeech(false);
	};

	const handleCancelSpeechEdit = () => {
		setEditSpeechText(scene.text);
		setIsEditingSpeech(false);
	};

	const handleSaveSubtitleText = () => {
		onUpdateSubtitleText(scene.id, editSubtitleText);
		setIsEditingSubtitle(false);
	};

	const handleCancelSubtitleEdit = () => {
		setEditSubtitleText(scene.subtitleText);
		setIsEditingSubtitle(false);
	};

	const handleCopyToSubtitle = () => {
		onCopyTextToSubtitle(scene.id);
		setEditSubtitleText(scene.text);
		toast.success("読み上げテキストをテロップにコピーしました");
	};

	const handleAudioDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleAudioDragEnter = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingAudio(true);
	}, []);

	const handleAudioDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingAudio(false);
	}, []);

	const handleAudioDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDraggingAudio(false);

			const file = e.dataTransfer.files[0];
			if (file && validateAudioFile(file)) {
				logger.info("音声ファイルをドロップ", {
					fileName: file.name,
					fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
					sceneId: scene.id,
				});
				onUploadAudio(scene.id, file);
			}
		},
		[onUploadAudio, scene.id, validateAudioFile]
	);

	// 音声ステータスの表示文言はここを唯一の情報源にする。
	// バッジと折りたたみ時の状態行で文言がずれないようにするため
	const AUDIO_STATUS_LABEL: Record<Scene["audioStatus"], string> = {
		none: "音声なし",
		done: "音声あり",
		generating: "生成中",
		error: "エラー",
	};

	const audioStatusBadge = () => {
		switch (scene.audioStatus) {
			case "done":
				return (
					<span className="badge badge-success">
						<Volume2 size={10} />
						{AUDIO_STATUS_LABEL.done}
					</span>
				);
			case "generating":
				return (
					<span className="badge badge-warning">
						<Loader2 size={10} className="animate-spin" />
						{AUDIO_STATUS_LABEL.generating}
					</span>
				);
			case "error":
				return (
					<span className="badge badge-error">
						<AlertCircle size={10} />
						{AUDIO_STATUS_LABEL.error}
					</span>
				);
			default:
				return null;
		}
	};

	const isSubtitleActive = globalSubtitleEnabled && scene.subtitleEnabled;

	// 折りたたみ時に見せる一行。モードによって主役のテキストが変わる
	const summaryText =
		editMode === "speech"
			? scene.text || "テキスト未設定"
			: scene.subtitleText || "テロップ未設定";
	const hasSummaryText =
		editMode === "speech" ? !!scene.text : !!scene.subtitleText;
	const audioStatusLabel =
		editMode === "speech"
			? AUDIO_STATUS_LABEL[scene.audioStatus]
			: scene.subtitleEnabled
				? "テロップ オン"
				: "テロップ オフ";


	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`scene-item ${isDragging ? "dragging" : ""} ${
				isDraggingAudio ? "ring-2 ring-coral-400 bg-coral-50" : ""
			}`}
			onDragOver={handleAudioDragOver}
			onDragEnter={handleAudioDragEnter}
			onDragLeave={handleAudioDragLeave}
			onDrop={handleAudioDrop}
		>
			{/* Drag Handle */}
			<button
				{...attributes}
				{...listeners}
				className="drag-handle drag-handle-row lg:self-stretch"
			>
				<GripVertical size={20} />
			</button>

			{/* Scene Number & Image with Subtitle Preview */}
			<div className="flex flex-col items-center gap-1 lg:gap-2">
				<span className="text-xs font-semibold text-warm-400">
					#{sceneOrder + 1}
				</span>
				<div className="scene-image">
					{/* Canvas ベースのプレビュー（動画と完全一致） */}
					{isSubtitleActive && thumbnailPreview ? (
						<img
							src={thumbnailPreview}
							alt={`Scene ${sceneOrder + 1} with subtitle`}
							className="block max-w-full max-h-full rounded"
						/>
					) : (
						<img
							src={scene.imageUrl}
							alt={`Scene ${sceneOrder + 1}`}
							className="block max-w-full max-h-full"
						/>
					)}
				</div>
			</div>

			{/* Content Area */}
			<div className="flex-1 min-w-0 space-y-1.5 lg:space-y-3">
				{/* Mode Content */}
				{editMode === "speech" ? (
					/* Speech Tab */
					<>
						{isEditingSpeech ? (
							<div className="space-y-2">
								<textarea
									value={editSpeechText}
									onChange={(e) =>
										setEditSpeechText(e.target.value)
									}
									className="textarea-edit"
									rows={2}
									autoFocus
									placeholder="読み上げテキストを入力..."
								/>
								<div className="flex gap-2">
									<button
										onClick={handleSaveSpeechText}
										className="btn-save"
									>
										<Check size={14} />
										保存
									</button>
									<button
										onClick={handleCancelSpeechEdit}
										className="btn-ghost"
									>
										<X size={14} />
										キャンセル
									</button>
								</div>
							</div>
						) : (
							<>
								{/* スマートフォン幅: 本文 2 行 + 状態一行。
								    畳んでも開いても高さが変わらないよう 2 行分を確保する */}
								<div className="flex items-start gap-2 lg:hidden">
									{/* 本文は読むためのもの。開閉はシェブロン、編集は鉛筆が担うので、
									    ここを押せる見た目にはしない */}
									<div className="scene-summary-tap flex-1 min-w-0">
										<p
											className={`scene-summary-text text-sm leading-snug line-clamp-2 ${
												!scene.text
													? "text-warm-400 italic"
													: "text-warm-700"
											}`}
										>
											{summaryText}
										</p>
										<p className="scene-summary-status mt-0.5 text-xs text-warm-400">
											{audioStatusLabel}
											{scene.audioDuration !== undefined &&
												` ・ ${totalTime.toFixed(1)}秒`}
										</p>
									</div>
									<button
										type="button"
										onClick={() => setExpanded(!expanded)}
										aria-expanded={expanded}
										aria-controls={timingControlsId}
										aria-label={
											expanded
												? "表示時間の調整を閉じる"
												: "表示時間の調整を開く"
										}
										className="btn-icon-flat shrink-0"
									>
										<ChevronDown
											size={18}
											className={`transition-transform ${
												expanded ? "rotate-180" : ""
											}`}
										/>
									</button>
								</div>

								<div className="flex items-start gap-2">
									<p
										className={`hidden lg:block text-base flex-1 leading-relaxed ${
											!scene.text
												? "text-warm-400 italic"
												: "text-warm-700"
										}`}
									>
										{scene.text || "テキスト未設定"}
									</p>
									{/* 編集は畳んでいても直接押せる位置に残す */}
									<div className="flex flex-1 justify-end gap-2 lg:flex-none lg:contents">
										<button
											onClick={() => setIsEditingSpeech(true)}
											className="btn-icon-coral shrink-0"
											title="テキスト編集"
										>
											<Pencil size={18} />
										</button>
										<button
											onClick={() => onDelete(scene.id)}
											className="shrink-0 inline-flex items-center justify-center w-[2.25rem] h-[2.25rem] text-warm-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
											title="シーンを削除"
										>
											<Trash2 size={18} />
										</button>
									</div>
								</div>
							</>
						)}

						{/* Row 2: Status badges */}
						<div className="flex items-center gap-2">
							{audioStatusBadge()}
							{scene.audioDuration !== undefined && (
								<span className="text-xs text-warm-400">
									{totalTime.toFixed(1)}秒
								</span>
							)}
						</div>

						{/* Row 3: Audio controls (always visible, disabled when not applicable) */}
						<div className="flex items-center gap-1 justify-end">
							{/* File I/O group */}
							<input
								ref={audioInputRef}
								type="file"
								accept="audio/*"
								onChange={handleAudioUpload}
								className="sr-only"
							/>
							<button
								onClick={() =>
									audioInputRef.current?.click()
								}
								className="btn-icon-flat"
								title="音声アップロード"
							>
								<Upload size={16} />
							</button>
							<button
								onClick={() => onDownloadAudio(scene.id)}
								disabled={!(scene.audioStatus === 'done' && scene.audioUrl)}
								className="btn-icon-flat"
								title="音声ダウンロード"
							>
								<Download size={16} />
							</button>

							<div className="w-px h-4 bg-warm-200" />

							{/* Audio generation/preview group */}
							<button
								onClick={() =>
									onRegenerateAudio(scene.id)
								}
								disabled={!scene.text || scene.audioStatus === 'generating'}
								className="btn-icon-flat"
								title="音声再生成"
							>
								<RefreshCw size={16} />
							</button>
							<button
								onClick={() => scene.audioUrl && onPlayAudio(scene.audioUrl, scene.id)}
								disabled={!(scene.audioStatus === 'done' && scene.audioUrl)}
								className="btn-icon-flat"
								title={playingAudioId === scene.id ? "音声停止" : "音声プレビュー"}
							>
								{playingAudioId === scene.id ? <Pause size={16} /> : <Play size={16} />}
							</button>

							<div className="w-px h-4 bg-warm-200" />

							{/* Destructive group */}
							<button
								onClick={() => onDeleteAudio(scene.id)}
								disabled={!(scene.audioStatus === 'done' && scene.audioUrl)}
								className="btn-icon-flat text-amber-500 hover:text-amber-600 hover:bg-amber-50"
								title="音声を削除"
							>
								<VolumeX size={16} />
							</button>
						</div>

						{/* Timing Controls: lg 未満で畳むのはこの間隔調整だけ。
						    高さは 0fr <-> 1fr で繋ぎ、外側を跳ねさせない */}
						<div
							id={timingControlsId}
							className={`collapsible ${
								expanded ? "collapsible-open" : ""
							}`}
						>
						<div>
						<div className="pt-1 border-t border-warm-200 space-y-1">
								{scene.audioDuration !== undefined && (
									<p className="text-xs text-warm-500">
										{sceneOrder > 0 &&
											`前: ${beforeTime.toFixed(1)}s + `}
										音声: {baseTime.toFixed(1)}s + 後:{" "}
										{afterTime.toFixed(1)}s ={" "}
										<span className="font-semibold text-coral-600">
											計 {totalTime.toFixed(1)}s
										</span>
									</p>
								)}
								{sceneOrder > 0 && (
									<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
										<label className="text-xs text-warm-500 w-16 shrink-0">
											前の間隔
										</label>
										<input
											type="range"
											min={ADDITIONAL_TIME.MIN}
											max={ADDITIONAL_TIME.MAX}
											step={ADDITIONAL_TIME.STEP}
											value={
												scene.additionalDisplayTimeBefore ??
												0
											}
											onChange={(e) =>
												onUpdateAdditionalTime(
													scene.id,
													"before",
													parseFloat(e.target.value)
												)
											}
											className="flex-1 min-w-24 h-1.5 bg-warm-200 rounded-full appearance-none cursor-pointer accent-coral-500"
										/>
										<input
											type="number"
											min={ADDITIONAL_TIME.MIN}
											max={ADDITIONAL_TIME.MAX}
											step={ADDITIONAL_TIME.STEP}
											value={
												scene.additionalDisplayTimeBefore ??
												0
											}
											onChange={(e) => {
												const parsed = parseFloat(e.target.value);
												// 入力途中の空欄などを0として確定させない
												if (Number.isNaN(parsed)) return;
												const value = Math.min(ADDITIONAL_TIME.MAX, Math.max(ADDITIONAL_TIME.MIN, parsed));
												onUpdateAdditionalTime(
													scene.id,
													"before",
													value
												);
											}}
											className="w-16 px-1.5 py-0.5 text-xs font-medium text-warm-600 text-right border border-warm-200 rounded-md bg-white focus:outline-none focus:border-coral-300"
										/>
										<span className="text-xs text-warm-500">s</span>
									</div>
								)}
								<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
									<label className="text-xs text-warm-500 w-16 shrink-0">
										後の間隔
									</label>
									<input
										type="range"
										min={ADDITIONAL_TIME.MIN}
										max={ADDITIONAL_TIME.MAX}
										step={ADDITIONAL_TIME.STEP}
										value={
											scene.additionalDisplayTimeAfter ??
											0
										}
										onChange={(e) =>
											onUpdateAdditionalTime(
												scene.id,
												"after",
												parseFloat(e.target.value)
											)
										}
										className="flex-1 min-w-24 h-1.5 bg-warm-200 rounded-full appearance-none cursor-pointer accent-coral-500"
									/>
									<input
										type="number"
										min={ADDITIONAL_TIME.MIN}
										max={ADDITIONAL_TIME.MAX}
										step={ADDITIONAL_TIME.STEP}
										value={
											scene.additionalDisplayTimeAfter ??
											0
										}
										onChange={(e) => {
											const parsed = parseFloat(e.target.value);
											// 入力途中の空欄などを0として確定させない
											if (Number.isNaN(parsed)) return;
											const value = Math.min(ADDITIONAL_TIME.MAX, Math.max(ADDITIONAL_TIME.MIN, parsed));
											onUpdateAdditionalTime(
												scene.id,
												"after",
												value
											);
										}}
										className="w-16 px-1.5 py-0.5 text-xs font-medium text-warm-600 text-right border border-warm-200 rounded-md bg-white focus:outline-none focus:border-coral-300"
									/>
									<span className="text-xs text-warm-500">s</span>
								</div>
							</div>
						</div>
						</div>
					</>
				) : (
					/* Subtitle Mode */
					<>
						{/* Subtitle Text (テキスト先) */}
						{isEditingSubtitle ? (
							<div className="space-y-2">
								<textarea
									value={editSubtitleText}
									onChange={(e) =>
										setEditSubtitleText(e.target.value)
									}
									className="textarea-edit"
									rows={2}
									autoFocus
									placeholder="テロップテキストを入力..."
								/>
								{subtitleValidation?.exceedsMaxLines && (
									<p className="text-xs text-red-600 flex items-center gap-1 font-medium">
										<AlertCircle size={12} />
										{subtitleValidation.actualLineCount}
										行になっています（2行まで）
									</p>
								)}
								<div className="flex gap-2">
									<button
										onClick={handleSaveSubtitleText}
										className="btn-save"
									>
										<Check size={14} />
										保存
									</button>
									<button
										onClick={handleCancelSubtitleEdit}
										className="btn-ghost"
									>
										<X size={14} />
										キャンセル
									</button>
								</div>
							</div>
						) : (
							<>
								{/* スマートフォン幅: テロップ 2 行 + 状態一行。
								    テロップモードには畳む対象がないので操作はすべて出す */}
								<div className="flex items-start gap-2 lg:hidden">
									<div className="scene-summary-tap flex-1 min-w-0">
										<p
											className={`scene-summary-text text-sm leading-snug line-clamp-2 ${
												hasSummaryText
													? "text-warm-700"
													: "text-warm-400 italic"
											}`}
										>
											{summaryText}
										</p>
										<p className="scene-summary-status mt-0.5 text-xs text-warm-400">
											{audioStatusLabel}
										</p>
									</div>
								</div>

								<div className="flex items-start gap-2">
								<div className="hidden lg:block flex-1">
									{scene.subtitleText ? (
										<div className="text-base leading-relaxed text-warm-700">
											{/* 検証結果の行を表示（実際の改行位置を反映） */}
											{subtitleValidation?.lines.map(
												(line, i) => (
													<div
														key={i}
														className="whitespace-nowrap overflow-hidden text-ellipsis"
													>
														{line}
													</div>
												)
											)}
										</div>
									) : (
										<p className="text-base leading-relaxed text-warm-400 italic">
											テロップ未設定
										</p>
									)}
									{subtitleValidation?.exceedsMaxLines && (
										<p className="text-xs text-red-600 mt-1 flex items-center gap-1 font-medium">
											<AlertCircle size={12} />
											{subtitleValidation.actualLineCount}
											行になっています（2行まで表示）
										</p>
									)}
								</div>
								<div className="flex flex-1 justify-end gap-2 lg:flex-none lg:contents">
									<button
										onClick={() => {
											setEditSubtitleText(scene.subtitleText);
											setIsEditingSubtitle(true);
										}}
										className="btn-icon-coral shrink-0"
										title="テロップ編集"
									>
										<Pencil size={18} />
									</button>
									<button
										onClick={() => onDelete(scene.id)}
										className="shrink-0 inline-flex items-center justify-center w-[2.25rem] h-[2.25rem] text-warm-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
										title="シーンを削除"
									>
										<Trash2 size={18} />
									</button>
								</div>
								</div>
							</>
						)}

						{/* Status & Actions Row (コントロール後)。
						    狭幅では縮めずに折り返す。文字を縦に潰さない */}
						<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
							<div className="flex min-w-0 flex-wrap items-center gap-2">
								<button
									onClick={() =>
										onToggleSubtitle(
											scene.id,
											!scene.subtitleEnabled
										)
									}
									className={`btn-small ${
										scene.subtitleEnabled
											? "bg-coral-100 text-coral-700 border-coral-200"
											: ""
									}`}
									title={
										scene.subtitleEnabled
											? "テロップをオフ"
											: "テロップをオン"
									}
								>
									{scene.subtitleEnabled ? (
										<Eye size={14} />
									) : (
										<EyeOff size={14} />
									)}
									{scene.subtitleEnabled ? "オン" : "オフ"}
								</button>
								{!globalSubtitleEnabled &&
									scene.subtitleEnabled && (
										<span className="text-xs text-amber-600">
											※全体がオフです
										</span>
									)}
							</div>
							<div className="flex items-center gap-2">
								<button
									onClick={handleCopyToSubtitle}
									className="btn-icon"
									title="読み上げテキストをコピー"
									disabled={!scene.text}
								>
									<Copy size={18} />
								</button>
								<button
									onClick={() =>
										onOpenSubtitleStyleModal(scene.id)
									}
									className={`btn-icon ${
										scene.subtitleStyle
											? "bg-coral-100 text-coral-600"
											: ""
									}`}
									title="スタイル調整"
								>
									<Palette size={18} />
								</button>
							</div>
						</div>
					</>
				)}

			</div>
		</div>
	);
}
